import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';

const SITE_URL = 'https://hng.world';
const LOCALES = ['en-in', 'en-us', 'en-gb', 'en-nl', 'en-ae'];

const SEO = ({ 
  title, 
  description, 
  path = '',
  type = 'website',
  image = '/images/hg-logo.png',
  structuredData = null
}) => {
  const { locale } = useParams();
  const fullTitle = `${title} | H&G — Horizon & Greenprint`;
  const canonicalUrl = `${SITE_URL}/${locale}${path}`;

  // Default Organization structured data
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "H&G (Horizon & Greenprint)",
    "alternateName": "Horizon & Greenprint",
    "url": SITE_URL,
    "logo": image,
    "description": "Global partner for Pharma Engineering, Sustainability & ESG, Finance, Capital Markets, and M&A advisory — serving Fortune 500 organizations.",
    "foundingDate": "2020",
    "sameAs": [
      "https://www.linkedin.com/showcase/hng-global/",
      "https://www.instagram.com/hngcxo/"
    ],
    "address": [
      {
        "@type": "PostalAddress",
        "streetAddress": "De Entree 232",
        "postalCode": "1101EE",
        "addressLocality": "Amsterdam",
        "addressCountry": "NL"
      },
      {
        "@type": "PostalAddress",
        "streetAddress": "Vinayak Apartment, Ground Floor, Tidke Colony",
        "addressLocality": "Nashik",
        "addressRegion": "Maharashtra",
        "postalCode": "422002",
        "addressCountry": "IN"
      },
      {
        "@type": "PostalAddress",
        "streetAddress": "Westport, Pan Card Club Road, Baner",
        "addressLocality": "Pune",
        "addressRegion": "Maharashtra",
        "postalCode": "411045",
        "addressCountry": "IN"
      }
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "email": "contact@hng.world"
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="H&G — Horizon & Greenprint" />
      <meta property="og:locale" content={locale?.replace('-', '_') || 'en_IN'} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Hreflang Tags for all locales */}
      {LOCALES.map((loc) => (
        <link
          key={loc}
          rel="alternate"
          hrefLang={loc}
          href={`${SITE_URL}/${loc}${path}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/en-in${path}`} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || organizationSchema)}
      </script>
    </Helmet>
  );
};

// Service page structured data generator
export const generateServiceSchema = (service) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": service.type,
  "name": service.name,
  "description": service.description,
  "provider": {
    "@type": "Organization",
    "name": "H&G (Horizon & Greenprint)",
    "url": "https://hng.world"
  },
  "areaServed": [
    { "@type": "Country", "name": "India" },
    { "@type": "Country", "name": "Netherlands" }
  ]
});

// Article structured data generator
export const generateArticleSchema = (article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title,
  "description": article.excerpt,
  "author": {
    "@type": "Person",
    "name": article.author
  },
  "publisher": {
    "@type": "Organization",
    "name": "H&G — Horizon & Greenprint",
    "logo": {
      "@type": "ImageObject",
      "url": "/images/hg-logo.png"
    }
  },
  "datePublished": article.date,
  "image": article.image
});

export default SEO;
